import { formatCents, formatPerFolio } from "@/utils/packageFeatures.js";
import { landingFacts } from "@/utils/landingCatalog.js";

/**
 * Lo que buscadores, bots y vistas previas (WhatsApp, Facebook, LinkedIn) leen
 * de la portada. Se escribe al pre-generarla (scripts/prerender.mjs), con el
 * catálogo del momento del build: igual que la página, nada de precios a mano.
 */

/** Dominio canónico: www.cfd-ipunto.com redirige aquí. */
export const SITE_URL = "https://cfd-ipunto.com";

export const SITE_NAME = "CFD-iPunto";

export const SEO_TITLE =
  "Facturación electrónica CFDI para contadores y pymes | CFD-iPunto";

/** Id del <script type="application/json"> con el catálogo pre-generado. */
export const PRERENDERED_CATALOG_ID = "lp-catalogo";

/** $230, $18.33: sin centavos cuando son cero. */
const pesos = (cents) =>
  cents % 100 === 0 ? formatCents(cents) : formatPerFolio(cents);

/**
 * Descripción para el resultado de búsqueda (~155 caracteres, lo que Google
 * enseña antes de cortar). Habla de contadores, pymes y precio por folio,
 * las tres búsquedas que se quieren ganar.
 */
export const seoDescription = (packages) => {
  const { catalog, bestPerFolio } = landingFacts(packages);
  const cheapest = catalog[0];
  if (!cheapest || !bestPerFolio) {
    return "Factura CFDI sin mensualidades: compra paquetes de folios y paga solo lo que usas. Panel para contadores y despachos con constancia y 32/D de cada emisor.";
  }
  return `Factura CFDI sin mensualidades: paquetes de folios desde ${pesos(cheapest.priceCents)} y hasta ${pesos(bestPerFolio.pricePerFolioCents)} por folio. Panel para contadores y despachos con constancia y 32/D de cada emisor.`;
};

/** "55 6363 7044" -> "+52 55 6363 7044", el formato que pide schema.org. */
const internationalPhone = (phone) =>
  phone.startsWith("+") ? phone : `+52 ${phone}`;

/**
 * Datos estructurados (JSON-LD): quién es la empresa, qué vende y a qué
 * precio, y las preguntas frecuentes de la portada.
 *
 * @param {{packages: object[], faq: {question: string, answer: string}[],
 *   phone: string, email: string}} data
 */
export const structuredData = ({ packages, faq, phone, email }) => {
  const { catalog } = landingFacts(packages);
  const organization = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: SITE_NAME,
    url: SITE_URL,
    logo: `${SITE_URL}/logo.png`,
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "customer service",
      telephone: internationalPhone(phone),
      email,
      areaServed: "MX",
      availableLanguage: "es",
    },
  };
  const website = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: SITE_NAME,
    url: SITE_URL,
    inLanguage: "es-MX",
  };
  const blocks = [organization, website];

  if (catalog.length > 0) {
    const prices = catalog.map((pkg) => pkg.priceCents / 100);
    blocks.push({
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: SITE_NAME,
      url: SITE_URL,
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web",
      inLanguage: "es-MX",
      description:
        "Facturación electrónica CFDI por paquetes de folios, con panel para contadores y despachos.",
      offers: {
        "@type": "AggregateOffer",
        priceCurrency: "MXN",
        lowPrice: Math.min(...prices),
        highPrice: Math.max(...prices),
        offerCount: catalog.length,
        offers: catalog.map((pkg) => ({
          "@type": "Offer",
          name: pkg.name,
          price: pkg.priceCents / 100,
          priceCurrency: "MXN",
          url: `${SITE_URL}/#precios`,
        })),
      },
    });
  }

  if (faq.length > 0) {
    blocks.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faq.map((item) => ({
        "@type": "Question",
        name: item.question,
        acceptedAnswer: { "@type": "Answer", text: item.answer },
      })),
    });
  }

  return blocks;
};

/**
 * JSON que se puede meter en un <script> sin que un "</script>" dentro de un
 * texto lo cierre antes de tiempo.
 */
export const jsonForScript = (value) =>
  JSON.stringify(value)
    .replace(/</g, "\\u003c")
    .replace(new RegExp("\u2028", "g"), "\\u2028")
    .replace(new RegExp("\u2029", "g"), "\\u2029");
